import { T, useT } from '@transifex/react';
import { Avatar, Text, VStack } from '@vidahealth/vida-ui';
import { useState } from 'react';
import { type SvgProps } from 'react-native-svg';

import GroupCoaching from '@/assets/icons/group-coaching-type.svg';
import IndividualCoaching from '@/assets/icons/individual-coaching-type.svg';
import { useOnboardingSteps, useRulesEngine } from '@/modules/rulesEngine';
import { ContactSupport, CtaButton, Screen, ScreenHeading, SelectableRow } from '@/modules/ui';

type Modality = 'individual' | 'group';

// Value + illustration only — the copy lives in the component so the literals
// sit directly inside `t('…')` and get picked up by `txjs-cli push` (the
// transifex extractor parses source statically; a dynamic `t(option.title)`
// would never be registered).
const OPTIONS: { value: Modality; Image: React.FC<SvgProps> }[] = [
  { value: 'individual', Image: IndividualCoaching },
  { value: 'group', Image: GroupCoaching },
];

/**
 * COACHING METHOD — individual vs group (Sparta's CoachingMethodScreen /
 * stylos's CoachingTypePage). Reached from the program list only when the
 * selected program offers both modalities; the pick is stored on the rules
 * engine and used in the enroll payload. Continue advances to the review.
 */
export function CoachingMethodScreen() {
  const t = useT();
  const { coachingModality, setCoachingModality } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();

  const [showRequiredError, setShowRequiredError] = useState(false);

  // Static UI copy with the literals inside `t(…)` so they're extractable.
  const copy: Record<Modality, { title: string; subtitle: string }> = {
    individual: {
      title: t('Personal Coaching'),
      subtitle: t('Reach your goals with your own personal coach.'),
    },
    group: {
      title: t('Group Coaching'),
      subtitle: t('Tap into motivation with other members who have similar health goals.'),
    },
  };

  const onSelect = (value: Modality) => {
    setCoachingModality(value);
    setShowRequiredError(false);
  };

  const onContinue = () => {
    if (!coachingModality) {
      setShowRequiredError(true);
      return;
    }
    goToNextStep({ from: '/step/coaching-method-screen' });
  };

  return (
    <Screen
      footer={
        <CtaButton onPress={onContinue} testID='coaching-method-continue'>
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading title={t('What type of coaching do you prefer?')} />

        {showRequiredError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Please choose a coaching type to continue.' />
          </Text>
        ) : null}

        <VStack space='lg'>
          {OPTIONS.map((option) => {
            const { title, subtitle } = copy[option.value];
            return (
              <SelectableRow
                key={option.value}
                isSelected={coachingModality === option.value}
                selectionRole='radio'
                onPress={() => onSelect(option.value)}
                accessibilityLabel={title}
                testID={`coaching-method-${option.value}`}
              >
                <Avatar size='lg' className='overflow-hidden bg-[#FDF6EE]'>
                  <option.Image width='100%' height='100%' preserveAspectRatio='xMidYMid slice' />
                </Avatar>
                <VStack space='xs' className='flex-1'>
                  <Text size='md' weight='semibold' className='text-typography-900'>
                    {title}
                  </Text>
                  <Text size='sm' className='text-typography-600'>
                    {subtitle}
                  </Text>
                </VStack>
              </SelectableRow>
            );
          })}
        </VStack>

        <ContactSupport showPhone />
      </VStack>
    </Screen>
  );
}
